import { loadFirebase } from "@/lib/firebaseLoader";
import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { HttpError, shouldRetryQuery } from "@/lib/queryRetryPolicy";
import { createAnonymousRemintPolicy } from "@/lib/anonymousRemint";

// Firebase is only needed when a request has already been rejected because a
// signed-in person's token rotated. Keeping that import out of the module
// graph lets the public market reader paint before the Firebase iframe and
// Google configuration request have completed.
async function getFreshIdTokenAfter401(): Promise<string | null> {
  const { getFreshIdToken } = await loadFirebase();
  return getFreshIdToken();
}

// Module-level auth token — set by useAuth when Firebase signs in.
// Included as Authorization header on every API call so the server can
// identify the user and apply the correct subscription tier.
let _authToken: string | null = null;

// Boot gate: react-query mounts and fires ~20 queries before Firebase has
// restored the session, producing a 401 burst on every cold load (and noise
// in server logs). authed fetches wait (max 4s) for the first auth
// resolution — signed-in OR signed-out — before their first attempt.
let _authResolved = false;
let _resolveAuthReady: () => void = () => {};
const _authReady: Promise<void> = new Promise((resolve) => {
  _resolveAuthReady = () => {
    _authResolved = true;
    resolve();
  };
});
const _authReadyWithTimeout: Promise<void> = Promise.race([
  _authReady,
  new Promise<void>((r) => setTimeout(r, 4000)),
]);

/**
 * Re-mint hook for anonymous (demo) sessions, injected by `demo-token.ts`.
 * Injected, not imported: that module already depends on this one.
 */
let _anonymousReminter: (() => Promise<string | null>) | null = null;

export function registerAnonymousReminter(fn: () => Promise<string | null>) {
  _anonymousReminter = fn;
}

/**
 * Single-flight + cooldown, so one dashboard load costs ONE mint rather than
 * four. The policy is a leaf module because this one cannot be imported by a
 * test runner -- see `anonymousRemint.ts`.
 */
const _remintPolicy = createAnonymousRemintPolicy({
  mint: async () => (_anonymousReminter ? _anonymousReminter() : null),
});

/** Called by useAuth once Firebase has resolved the initial auth state. */
export function markAuthResolved() {
  _resolveAuthReady();
}

export async function waitForAuthReady(): Promise<void> {
  if (_authResolved) return;
  await _authReadyWithTimeout;
}

export function setAuthToken(token: string | null) {
  _authToken = token;
  if (token) _resolveAuthReady();
}

export function getAuthToken(): string | null {
  return _authToken;
}

/**
 * Gets the base URL for the Express API server (e.g., "https://spectra-mobile.vercel.app")
 * Falls back to relative URLs (empty string) for same-origin web deployments.
 * @returns {string} The API base URL, or empty string for same-origin
 */
export function getApiUrl(): string {
  // Web and its API are deployed together. Follow the actual origin so preview
  // builds use their proxy and production never creates cross-origin requests
  // merely because the compiled hostname differs from the serving hostname.
  if (typeof window !== "undefined" && window.location?.origin && /^https?:$/.test(window.location.protocol)) {
    return window.location.origin;
  }
  let host = (process.env.EXPO_PUBLIC_DOMAIN || "").trim();

  if (!host) {
    // In web build served by the same Express server, use relative paths
    if (typeof window !== "undefined" && typeof window.location !== "undefined") {
      return window.location.origin;
    }
    // Native fallback: production API server
    return "https://spectra-api-server-production.up.railway.app";
  }

  // Strip any protocol the user may have included
  host = host.replace(/^https?:\/\//, "");

  // Use https for all production domains
  const protocol = host.includes("localhost") ? "http" : "https";
  const url = new URL(`${protocol}://${host}`);

  // Remove trailing slash
  return url.origin;
}

export function getWebSocketUrl(): string {
  // Browser builds are served by the API origin. Resolve the socket from the
  // actual tab host so preview/custom domains cannot inherit a stale
  // EXPO_PUBLIC_DOMAIN and create a DNS failure during boot.
  if (typeof window !== "undefined" && window.location?.host) {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    return `${protocol}://${window.location.host}/ws/market`;
  }
  const apiUrl = getApiUrl();
  if (!apiUrl) return "ws://localhost:5000/ws/market";
  const wsProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
  const wsHost = apiUrl.replace(/^https?:\/\//, "");
  return `${wsProtocol}://${wsHost}/ws/market`;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const retryAfterHeader = res.headers.get("Retry-After");
    const retryAfter = retryAfterHeader === null ? Number.NaN : Number(retryAfterHeader);
    throw new HttpError(
      res.status,
      `${res.status}: ${text}`,
      Number.isFinite(retryAfter) ? retryAfter : null,
    );
  }
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  if (_authToken) headers["Authorization"] = `Bearer ${_authToken}`;
  return headers;
}

/**
 * Send an authenticated request and recover once when Firebase has rotated a
 * token between the client cache and server verification. A 401 is rejected by
 * middleware before a write route runs, so retrying once cannot duplicate a
 * checkout or another mutation.
 */
async function authenticatedFetch(url: string, init?: RequestInit): Promise<Response> {
  await waitForAuthReady();

  const send = () => fetch(url, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      ...authHeaders(),
    },
    credentials: "include",
  });

  const response = await send();
  if (response.status !== 401) return response;

  const freshToken = await getFreshIdTokenAfter401();
  if (freshToken) {
    setAuthToken(freshToken);
    return send();
  }

  /*
    🔴 THE ANONYMOUS VISITOR HAD NO WAY BACK (measured 2026-09-05).

    Above this line recovers a SIGNED-IN user by refreshing Firebase. An
    anonymous visitor on the open app has no Firebase session, so
    `getFreshIdToken()` returns null and this function used to return the 401
    unchanged. Their credential is the DEMO TOKEN, which carries a 24h TTL
    (`DEMO_TOKEN_TTL_SECONDS`) and was minted exactly once, by `bootAsGuest()`.

    So a tab left open overnight came back to a dashboard whose three data
    panels 401 forever: /api/market/snapshot, /api/case-studies and
    /api/forecast/72h. Measured in production over 3.4 days: 13,275 refusals,
    93.7-99.0% of all traffic to those three endpoints — and only 3 mint
    attempts in a window carrying 57 gated requests. Clients were not failing
    to mint; nothing was asking them to.

    ⚠️ Scope honestly: that volume came from essentially ONE stale tab, not a
    population of visitors. It is a latent scaling defect, not a live outage.

    The reminter is INJECTED rather than imported: `demo-token.ts` already
    imports `getApiUrl`/`setAuthToken` from this module, so importing it back
    would be a cycle. Same shape as `markAuthResolved()` above.
  */
  if (!_anonymousReminter) return response;
  return recoverAnonymousSession(response, send);
}

/**
 * Re-mint an anonymous visitor's demo token after a 401, then retry once.
 *
 * TODO(policy): implement. See the trade-offs in the accompanying discussion.
 *
 * Constraints this must respect, all measured:
 *  - A dashboard load fires FOUR gated requests at once. Four concurrent
 *    mints hit `rateLimitMiddleware("auth")`, which is 10/min per IP — the
 *    observed 401,401,401,401,429,429,429,429 storm.
 *  - `mintDemoToken()` returns null on failure and never throws.
 *  - A re-mint that succeeds but still yields 401 must NOT retry forever.
 */
async function recoverAnonymousSession(
  response: Response,
  send: () => Promise<Response>,
): Promise<Response> {
  // EXACTLY ONE retry. If it still 401s the caller sees the refusal, which is
  // the honest outcome -- a second mint would not change it.
  const retry = await _remintPolicy.shouldRetryAfterRemint();
  return retry ? send() : response;
}

export async function fetchWithAuth(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  return authenticatedFetch(url, init);
}

export async function apiRequest(
  method: string,
  route: string,
  data?: unknown | undefined,
): Promise<Response> {
  const baseUrl = getApiUrl();
  const url = new URL(route, baseUrl);

  // Same boot gate as `getQueryFn` and `fetchWithAuth`. Without it this helper
  // read `_authToken` before Firebase had restored the session and sent the
  // request unauthenticated: `usePositions` supplies its own queryFn built on
  // apiRequest, so `/api/positions` 401'd on every cold load (observed in
  // production 2026-08-02) while sibling queries on the default queryFn — which
  // does wait — succeeded. `connected` is derived from that response, so the
  // race surfaced as the Floor header disagreeing with its own body about
  // whether a portfolio existed. Resolves immediately once auth has settled and
  // is capped at 4s, so an anonymous request is delayed at most once per boot.
  const res = await authenticatedFetch(url.toString(), {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const baseUrl = getApiUrl();
    const url = new URL(queryKey.join("/") as string, baseUrl);

    const res = await authenticatedFetch(url.toString());

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }),
      refetchInterval: false,
      // Focus-refetch caused a 429 storm in prod (2026-07-09): ~20 queries
      // are stale on every alt-tab, and the server's per-category rate
      // limits (agent: was 30/min) turned each window switch into an outage.
      // Per-hook refetchInterval carries freshness instead.
      refetchOnWindowFocus: false,
      staleTime: 30000,
      gcTime: 5 * 60 * 1000, // Keep inactive data in cache for 5 minutes
      retry: shouldRetryQuery,
      retryDelay: 2000,
    },
    mutations: {
      retry: false,
    },
  },
});

// Per-route cache tuning — match server-side TTLs for optimal cache coherency
queryClient.setQueryDefaults(["/api/alerts"], { staleTime: 15000 });
queryClient.setQueryDefaults(["/api/dashboard"], { staleTime: 15000 });
queryClient.setQueryDefaults(["/api/market/snapshot"], { staleTime: 5000 });
queryClient.setQueryDefaults(["/api/spectra/realtime"], { staleTime: 5000 });
queryClient.setQueryDefaults(["/api/billing/subscription"], { staleTime: 5 * 60 * 1000 });
queryClient.setQueryDefaults(["/api/intelligence"], { staleTime: 30000 });
queryClient.setQueryDefaults(["/agent/unusual-money-movement"], { staleTime: 30000 });
queryClient.setQueryDefaults(["/api/sentiment/history"], { staleTime: 30000 });
queryClient.setQueryDefaults(["/api/correlation/cross-asset"], { staleTime: 30000 });
queryClient.setQueryDefaults(["/api/contagion-map"], { staleTime: 60000 });
queryClient.setQueryDefaults(["/api/contagion-map/portfolio-impact"], { staleTime: 60000 });
queryClient.setQueryDefaults(["/api/contagion-map/historical"], { staleTime: 120000 });
queryClient.setQueryDefaults(["/api/contagion-map/watchlists"], { staleTime: 120000 });
queryClient.setQueryDefaults(["/api/watchlists"], { staleTime: 30000 });
queryClient.setQueryDefaults(["/api/contagion-map/implied-correlation"], { staleTime: 120000 });
queryClient.setQueryDefaults(["/api/contagion-map/causal-attribution"], { staleTime: 120000 });
queryClient.setQueryDefaults(["/api/contagion-map/timemachine"], { staleTime: 300000 });
queryClient.setQueryDefaults(["/api/temporal/engines"], { staleTime: 60000 });
queryClient.setQueryDefaults(["/api/temporal/backtest"], { staleTime: 300000 });
queryClient.setQueryDefaults(["/api/temporal/risk"], { staleTime: 60000 });
queryClient.setQueryDefaults(["/api/temporal/signals"], { staleTime: 15000 });
